// ---------------------------------------------------------------------------
// Sam3SegmentationPanel — Panel de segmentación automática con SAM 3
//
// Uso: sube una imagen aérea (archivo o URL), escribe los prompts de texto
// (ej. "house", "building") y SAM 3 detecta todos los polígonos automáticamente.
// ---------------------------------------------------------------------------

import {
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
  ChangeEvent,
} from 'react';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Collapse,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  Row,
  Badge,
  Spinner,
  Table,
} from 'reactstrap';
import useSam3 from '../hooks/useSam3';
import { Sam3DetectedPolygon } from '../models/sam3Model';
import {
  applySimplification,
  SimplifyMode,
} from '../utils/polygonSimplify';

// ---------------------------------------------------------------------------
// Colores distintos por prompt label
// ---------------------------------------------------------------------------
const PALETTE = [
  'rgba(255, 99, 132, 0.55)',
  'rgba(54, 162, 235, 0.55)',
  'rgba(255, 206, 86, 0.55)',
  'rgba(75, 192, 192, 0.55)',
  'rgba(153, 102, 255, 0.55)',
  'rgba(255, 159, 64, 0.55)',
];

const STROKE_PALETTE = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
];

// ---------------------------------------------------------------------------
// Canvas drawing helper
// ---------------------------------------------------------------------------
const drawPolygonsOnCanvas = (
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  polygons: Sam3DetectedPolygon[],
  naturalW: number,
  naturalH: number,
) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Escalar coordenadas de pixel original → tamaño del canvas
  const scaleX = canvas.width / naturalW;
  const scaleY = canvas.height / naturalH;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  // Índice de color por prompt
  const promptIndex: Record<string, number> = {};
  let colorCounter = 0;

  polygons.forEach((polygon) => {
    if (!(polygon.promptLabel in promptIndex)) {
      promptIndex[polygon.promptLabel] = colorCounter % PALETTE.length;
      colorCounter++;
    }
    const idx = promptIndex[polygon.promptLabel];

    if (!polygon.points || polygon.points.length < 3) return;

    ctx.beginPath();
    const [firstX, firstY] = polygon.points[0];
    ctx.moveTo(firstX * scaleX, firstY * scaleY);

    for (let i = 1; i < polygon.points.length; i++) {
      const [px, py] = polygon.points[i];
      ctx.lineTo(px * scaleX, py * scaleY);
    }
    ctx.closePath();

    ctx.fillStyle = PALETTE[idx];
    ctx.fill();
    ctx.strokeStyle = STROKE_PALETTE[idx];
    ctx.lineWidth = 2;
    ctx.stroke();

    // Label de confianza en el centroide
    const centroidX =
      (polygon.points.reduce((sum, p) => sum + p[0], 0) /
        polygon.points.length) *
      scaleX;
    const centroidY =
      (polygon.points.reduce((sum, p) => sum + p[1], 0) /
        polygon.points.length) *
      scaleY;

    ctx.font = 'bold 11px sans-serif';
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.fillText(
      `${polygon.promptLabel} ${(polygon.confidence * 100).toFixed(0)}%`,
      centroidX,
      centroidY,
    );
  });
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  defaultPrompts?: string[];
}

const Sam3SegmentationPanel = ({ defaultPrompts }: Props) => {
  const {
    isLoading,
    error,
    polygons,
    promptsConfig,
    setPromptsConfig,
    threshold,
    setThreshold,
    segmentFromUrl,
    segmentFromFile,
    clearResults,
  } = useSam3({ prompts: defaultPrompts });

  // Canvas refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const pendingFileRef = useRef<File | null>(null);

  // Image source state
  const [imageUrl, setImageUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  // New prompt tag being typed
  const [newPrompt, setNewPrompt] = useState('');

  // Debug panel
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugMode, setDebugMode] = useState<'table' | 'json'>('table');

  // Simplification
  const [simplifyMode, setSimplifyMode] = useState<SimplifyMode>('none');
  const [simplifyEpsilon, setSimplifyEpsilon] = useState(5);

  const displayPolygons = useMemo(
    () =>
      polygons.map((p) => ({
        ...p,
        points: applySimplification(p.points, simplifyMode, simplifyEpsilon),
      })),
    [polygons, simplifyMode, simplifyEpsilon],
  );

  // ---------------------------------------------------------------------------
  // Draw polygons whenever they change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!canvasRef.current || !imageRef.current || !displayPolygons.length) return;
    drawPolygonsOnCanvas(
      canvasRef.current,
      imageRef.current,
      displayPolygons,
      naturalSize.w,
      naturalSize.h,
    );
  }, [displayPolygons, naturalSize]);

  // ---------------------------------------------------------------------------
  // Load image on canvas when previewUrl changes
  // ---------------------------------------------------------------------------
  const loadImageOnCanvas = useCallback((src: string) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });

      if (!canvasRef.current) return;
      // Fit canvas to container width, mantaining aspect ratio
      const maxW = canvasRef.current.parentElement?.clientWidth ?? 800;
      const ratio = img.naturalHeight / img.naturalWidth;
      canvasRef.current.width = maxW;
      canvasRef.current.height = maxW * ratio;

      const ctx = canvasRef.current.getContext('2d');
      ctx?.drawImage(img, 0, 0, canvasRef.current.width, canvasRef.current.height);
    };
    img.src = src;
  }, []);

  useEffect(() => {
    if (previewUrl) loadImageOnCanvas(previewUrl);
  }, [previewUrl, loadImageOnCanvas]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    clearResults();
    // Precarga en canvas para preview y guarda referencia al file para segmentación
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setImageUrl('');
    // Almacena el file para que handleSegment pueda usar segmentFromFile
    pendingFileRef.current = file;
  };

  const handleUrlLoad = () => {
    if (!imageUrl.trim()) return;
    clearResults();
    pendingFileRef.current = null;
    setPreviewUrl(imageUrl.trim());
  };

  const handleAddPrompt = () => {
    const trimmed = newPrompt.trim();
    if (!trimmed || promptsConfig.includes(trimmed)) return;
    const updated = [...promptsConfig, trimmed];
    setPromptsConfig(updated);
    setNewPrompt('');
  };

  const handleRemovePrompt = (prompt: string) => {
    setPromptsConfig(promptsConfig.filter((p) => p !== prompt));
  };

  const handleSegment = async () => {
    if (!previewUrl) return;
    // Si hay un archivo local pendiente, enviarlo directamente (sin re-fetch)
    if (pendingFileRef.current) {
      await segmentFromFile(pendingFileRef.current);
    } else {
      // URL externa → convertir a base64 via canvas
      await segmentFromUrl(previewUrl);
    }
  };

  const handleClear = () => {
    clearResults();
    if (canvasRef.current && imageRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.drawImage(
        imageRef.current,
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height,
      );
    }
  };

  // Agrupar polígonos por prompt para el resumen (sobre los ya simplificados)
  const polygonsByPrompt = displayPolygons.reduce<Record<string, Sam3DetectedPolygon[]>>(
    (acc, p) => {
      if (!acc[p.promptLabel]) acc[p.promptLabel] = [];
      acc[p.promptLabel].push(p);
      return acc;
    },
    {},
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Card>
      <CardHeader>
        <Row className="align-items-center">
          <Col>
            <h3 className="mb-0">
              <i className="ni ni-image mr-2" />
              Segmentación automática con SAM 3
            </h3>
            <small className="text-muted">
              Detecta techos y edificios en imágenes aéreas usando IA (Roboflow SAM 3)
            </small>
          </Col>
        </Row>
      </CardHeader>

      <CardBody>
        <Row>
          {/* ----------------------------------------------------------------
              Panel de controles (izquierda)
          ----------------------------------------------------------------- */}
          <Col md="3">
            {/* Imagen por URL */}
            <FormGroup>
              <label className="form-control-label">URL de imagen aérea</label>
              <InputGroup>
                <Input
                  placeholder="https://..."
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUrlLoad()}
                />
                <InputGroupAddon addonType="append">
                  <Button color="primary" outline onClick={handleUrlLoad}>
                    Cargar
                  </Button>
                </InputGroupAddon>
              </InputGroup>
              <small className="text-muted">
                O sube un archivo directamente:
              </small>
            </FormGroup>

            {/* Imagen por archivo */}
            <FormGroup>
              <label className="form-control-label">Subir archivo</label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
            </FormGroup>

            {/* Prompts de texto */}
            <FormGroup>
              <label className="form-control-label">
                Prompts de segmentación
              </label>
              <div className="d-flex flex-wrap mb-2" style={{ gap: '6px' }}>
                {promptsConfig.map((prompt) => (
                  <Badge
                    key={prompt}
                    color="primary"
                    pill
                    style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    onClick={() => handleRemovePrompt(prompt)}
                  >
                    {prompt} &times;
                  </Badge>
                ))}
              </div>
              <InputGroup>
                <Input
                  placeholder="house, building..."
                  value={newPrompt}
                  onChange={(e) => setNewPrompt(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddPrompt()}
                />
                <InputGroupAddon addonType="append">
                  <Button color="success" outline onClick={handleAddPrompt}>
                    +
                  </Button>
                </InputGroupAddon>
              </InputGroup>
              <small className="text-muted">
                Presiona Enter o + para agregar. Clic en badge para eliminar.
              </small>
            </FormGroup>

            {/* Umbral de confianza */}
            <FormGroup>
              <label className="form-control-label">
                Umbral de confianza: <strong>{(threshold * 100).toFixed(0)}%</strong>
              </label>
              <Input
                type="range"
                min={0.1}
                max={0.9}
                step={0.05}
                value={threshold}
                onChange={(e) => setThreshold(parseFloat(e.target.value))}
                style={{ padding: 0 }}
              />
              <small className="text-muted">
                Menor = más detecciones, Mayor = más precisión
              </small>
            </FormGroup>

            {/* Simplificación de polígonos */}
            <FormGroup>
              <label className="form-control-label">Simplificación de polígonos</label>
              <div className="d-flex" style={{ gap: 6 }}>
                {(['none', 'simplify', 'bbox'] as SimplifyMode[]).map((mode) => (
                  <Button
                    key={mode}
                    size="sm"
                    color={simplifyMode === mode ? 'primary' : 'secondary'}
                    outline={simplifyMode !== mode}
                    onClick={() => setSimplifyMode(mode)}
                  >
                    {mode === 'none' && 'Original'}
                    {mode === 'simplify' && 'Simplificar'}
                    {mode === 'bbox' && 'Rectángulo'}
                  </Button>
                ))}
              </div>
              {simplifyMode === 'simplify' && (
                <div className="mt-2">
                  <small className="text-muted d-block mb-1">
                    Tolerancia: <strong>{simplifyEpsilon}px</strong>
                    {' '}— mayor = menos puntos
                  </small>
                  <Input
                    type="range"
                    min={1}
                    max={30}
                    step={1}
                    value={simplifyEpsilon}
                    onChange={(e) => setSimplifyEpsilon(parseInt(e.target.value))}
                    style={{ padding: 0 }}
                  />
                </div>
              )}
              {simplifyMode === 'bbox' && (
                <small className="text-muted d-block mt-1">
                  Cada polígono se convierte en su rectángulo envolvente (4 puntos)
                </small>
              )}
            </FormGroup>

            {/* Resumen de resultados */}
            {polygons.length > 0 && (
              <div className="mt-3">
                <h5>Resultado</h5>
                {Object.entries(polygonsByPrompt).map(([label, polys], idx) => (
                  <div key={label} className="d-flex justify-content-between">
                    <span>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 12,
                          height: 12,
                          backgroundColor: STROKE_PALETTE[idx % STROKE_PALETTE.length],
                          borderRadius: 2,
                          marginRight: 6,
                        }}
                      />
                      {label}
                    </span>
                    <Badge color="secondary">{polys.length}</Badge>
                  </div>
                ))}
                <hr />
                <div className="d-flex justify-content-between">
                  <strong>Total</strong>
                  <Badge color="primary">{polygons.length}</Badge>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="alert alert-danger mt-3" role="alert">
                <i className="ni ni-fat-remove mr-2" />
                {error}
              </div>
            )}
          </Col>

          {/* ----------------------------------------------------------------
              Canvas (derecha)
          ----------------------------------------------------------------- */}
          <Col md="9">
            <div
              style={{
                position: 'relative',
                minHeight: 300,
                backgroundColor: '#f4f5f7',
                borderRadius: 8,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isLoading && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(255,255,255,0.75)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                  }}
                >
                  <Spinner color="primary" style={{ width: 48, height: 48 }} />
                  <p className="mt-3 text-primary font-weight-bold">
                    Analizando imagen con SAM 3…
                  </p>
                </div>
              )}

              {!previewUrl && (
                <div className="text-center text-muted py-5">
                  <i
                    className="ni ni-image"
                    style={{ fontSize: 64, opacity: 0.3 }}
                  />
                  <p className="mt-3">
                    Carga una imagen aérea para comenzar la segmentación
                  </p>
                </div>
              )}

              <canvas
                ref={canvasRef}
                style={{
                  display: previewUrl ? 'block' : 'none',
                  width: '100%',
                  height: 'auto',
                  borderRadius: 8,
                }}
              />
            </div>

            {/* Leyenda de colores */}
            {polygons.length > 0 && (
              <div className="mt-2 d-flex flex-wrap" style={{ gap: 8 }}>
                {Object.entries(polygonsByPrompt).map(([label, polys], idx) => (
                  <span
                    key={label}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 10px',
                      borderRadius: 20,
                      backgroundColor: PALETTE[idx % PALETTE.length],
                      border: `2px solid ${STROKE_PALETTE[idx % STROKE_PALETTE.length]}`,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {label}: {polys.length}
                  </span>
                ))}
              </div>
            )}
          </Col>
        </Row>
      </CardBody>

      <CardFooter>
        <Row>
          <Col>
            <Button
              color="primary"
              onClick={handleSegment}
              disabled={isLoading || !previewUrl || !promptsConfig.length}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Segmentando...
                </>
              ) : (
                <>
                  <i className="ni ni-vector mr-2" />
                  Segmentar imagen
                </>
              )}
            </Button>

            {polygons.length > 0 && (
              <Button
                color="secondary"
                outline
                className="ml-2"
                onClick={handleClear}
                disabled={isLoading}
              >
                <i className="ni ni-fat-remove mr-2" />
                Limpiar resultados
              </Button>
            )}

            {polygons.length > 0 && (
              <Button
                color="info"
                outline
                className="ml-2"
                onClick={() => setDebugOpen((o) => !o)}
              >
                <i className={`ni ni-${debugOpen ? 'bold-up' : 'bold-down'} mr-2`} />
                {debugOpen ? 'Ocultar' : 'Ver'} datos raw
              </Button>
            )}
          </Col>

          {polygons.length > 0 && (
            <Col className="text-right">
              <small className="text-muted">
                {polygons.length} polígono(s) detectado(s) con umbral{' '}
                {(threshold * 100).toFixed(0)}%
              </small>
            </Col>
          )}
        </Row>

        {/* ----------------------------------------------------------------
            Panel de debug
        ----------------------------------------------------------------- */}
        <Collapse isOpen={debugOpen}>
          <div className="mt-3 p-3" style={{ backgroundColor: '#f4f5f7', borderRadius: 8 }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">
                <i className="ni ni-align-left-2 mr-2" />
                Datos de predicciones ({displayPolygons.length} polígonos
                {simplifyMode !== 'none' && (
                  <Badge color="info" className="ml-2" style={{ fontSize: 10 }}>
                    {simplifyMode === 'bbox' ? 'rectángulo' : `simplificado ε=${simplifyEpsilon}`}
                  </Badge>
                )}
                )
              </h5>
              <div>
                <Button
                  size="sm"
                  color={debugMode === 'table' ? 'primary' : 'secondary'}
                  outline={debugMode !== 'table'}
                  className="mr-1"
                  onClick={() => setDebugMode('table')}
                >
                  Tabla
                </Button>
                <Button
                  size="sm"
                  color={debugMode === 'json' ? 'primary' : 'secondary'}
                  outline={debugMode !== 'json'}
                  onClick={() => setDebugMode('json')}
                >
                  JSON
                </Button>
              </div>
            </div>

            {debugMode === 'table' ? (
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                <Table size="sm" bordered hover responsive className="mb-0 bg-white">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Prompt</th>
                      <th>Confianza</th>
                      <th>Puntos</th>
                      <th>Primer punto</th>
                      <th>Último punto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayPolygons.map((poly, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 10,
                              height: 10,
                              backgroundColor: STROKE_PALETTE[
                                Object.keys(polygonsByPrompt).indexOf(poly.promptLabel) %
                                  STROKE_PALETTE.length
                              ],
                              borderRadius: 2,
                              marginRight: 5,
                            }}
                          />
                          {poly.promptLabel}
                        </td>
                        <td>
                          <Badge
                            color={
                              poly.confidence >= 0.7
                                ? 'success'
                                : poly.confidence >= 0.4
                                ? 'warning'
                                : 'danger'
                            }
                          >
                            {(poly.confidence * 100).toFixed(1)}%
                          </Badge>
                        </td>
                        <td>{poly.points.length}</td>
                        <td>
                          <code style={{ fontSize: 11 }}>
                            [{poly.points[0]?.[0].toFixed(1)}, {poly.points[0]?.[1].toFixed(1)}]
                          </code>
                        </td>
                        <td>
                          <code style={{ fontSize: 11 }}>
                            [{poly.points[poly.points.length - 1]?.[0].toFixed(1)},{' '}
                            {poly.points[poly.points.length - 1]?.[1].toFixed(1)}]
                          </code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <pre
                style={{
                  maxHeight: 320,
                  overflowY: 'auto',
                  fontSize: 11,
                  backgroundColor: '#1e1e2e',
                  color: '#cdd6f4',
                  padding: 12,
                  borderRadius: 6,
                  margin: 0,
                }}
              >
                {JSON.stringify(displayPolygons, null, 2)}
              </pre>
            )}
          </div>
        </Collapse>
      </CardFooter>
    </Card>
  );
};

export default Sam3SegmentationPanel;
